import { defineNuxtPlugin } from '#app';
import { createLogger } from '~/utils/logger';

const logger = createLogger('error-reporter');

type AnyError = unknown & {
    statusCode?: number | string;
    status?: number | string;
    response?: { status?: number | string };
    cause?: unknown;
};

type ReportClientError = (error: AnyError, extraContext?: Record<string, unknown>) => void;
export type { ReportClientError };

declare module '#app' {
    interface NuxtApp {
        $reportClientError: ReportClientError;
    }
}

declare module 'vue' {
    interface ComponentCustomProperties {
        $reportClientError: ReportClientError;
    }
}

const MESSAGE_TYPE = 'APPLICATION_ERROR';
const RECOVERY_STORAGE_KEY = 'fanatics:error-reporter:recovery';
const RECOVERY_MAX_ATTEMPTS = 2;
const RECOVERY_WINDOW_MS = 2 * 60 * 1000;

type RecoveryState = {
    attempts: number;
    lastAttempt: number;
};

type RecoveryPlan = null | {
    meta: {
        recoveryAction: 'self-reload' | 'request-parent-refresh';
        attempt: number;
        maxAttempts: number;
    };
    execute?: () => void;
};

const toNumber = (value?: number | string | null): number | null => {
    if (value === undefined || value === null) return null;
    const asNumber = typeof value === 'string' ? Number(value) : value;
    return Number.isFinite(asNumber) ? (asNumber as number) : null;
};

const extractStatusCode = (error: AnyError): number | null => {
    if (!error) return null;

    if (typeof error === 'number' && Number.isFinite(error)) return error;
    if (typeof error === 'string' && Number.isFinite(Number(error))) return Number(error);

    const fromDirect = toNumber(error.statusCode ?? error.status);
    if (fromDirect) return fromDirect;

    const fromResponse = toNumber(error.response?.status);
    if (fromResponse) return fromResponse;

    if (error.cause && typeof error.cause === 'object') {
        return extractStatusCode(error.cause as AnyError);
    }

    return null;
};

const toError = (error: AnyError): Error => {
    if (error instanceof Error) return error;
    const message =
        (typeof error === 'string' && error) ||
        (error && (error as { message?: string }).message) ||
        'Unknown error';
    return new Error(message);
};

const shouldDeduplicate = (extraContext: Record<string, unknown>): boolean =>
    extraContext.dedupe !== false;

export default defineNuxtPlugin((nuxtApp) => {
    if (import.meta.server) return;

    const { $datadogRum } = nuxtApp;
    const reportedSignatures = new Set<string>();

    const reportToDatadog = (error: Error, context: Record<string, unknown>) => {
        $datadogRum?.addError?.(error, context);
        logger.error('Application error reported', { event: 'application_error', ...context }, error);
    };

    const notifyParent = (payload: Record<string, unknown>) => {
        if (window.parent === window) return; // no parent to notify
        window.parent.postMessage({ type: MESSAGE_TYPE, data: payload }, '*');
        logger.info('Reported client error payload to parent', payload);
    };

    const readRecoveryState = (): RecoveryState => {
        const fallback: RecoveryState = { attempts: 0, lastAttempt: 0 };
        if (typeof window === 'undefined') return fallback;
        try {
            const raw = window.sessionStorage?.getItem(RECOVERY_STORAGE_KEY);
            if (!raw) return fallback;
            const parsed = JSON.parse(raw) as RecoveryState;
            if (typeof parsed?.attempts === 'number' && typeof parsed?.lastAttempt === 'number') {
                return parsed;
            }
            return fallback;
        } catch (error) {
            logger.warn('Failed to read error recovery state', error);
            return fallback;
        }
    };

    const writeRecoveryState = (state: RecoveryState) => {
        if (typeof window === 'undefined') return;
        try {
            window.sessionStorage?.setItem(RECOVERY_STORAGE_KEY, JSON.stringify(state));
        } catch (error) {
            logger.warn('Failed to persist error recovery state', error);
        }
    };

    const clearClientCaches = async (stateSnapshot: string | null) => {
        const tasks: Promise<unknown>[] = [];

        if ('caches' in window && typeof window.caches?.keys === 'function') {
            tasks.push(
                window.caches
                    .keys()
                    .then((keys) => Promise.all(keys.map((key) => window.caches.delete(key))))
                    .catch((error) => {
                        logger.warn('Failed to clear CacheStorage during recovery', error);
                    })
            );
        }

        try {
            window.localStorage?.clear();
        } catch (error) {
            logger.warn('Failed to clear localStorage during recovery', error);
        }

        try {
            window.sessionStorage?.clear();
        } catch (error) {
            logger.warn('Failed to clear sessionStorage during recovery', error);
        } finally {
            if (stateSnapshot) {
                try {
                    window.sessionStorage?.setItem(RECOVERY_STORAGE_KEY, stateSnapshot);
                } catch (error) {
                    logger.warn('Failed to restore recovery state snapshot', error);
                }
            }
        }

        await Promise.allSettled(tasks);
    };

    const createRecoveryPlan = (): RecoveryPlan => {
        if (typeof window === 'undefined') return null;

        const now = Date.now();
        const state = readRecoveryState();
        const withinWindow = now - state.lastAttempt <= RECOVERY_WINDOW_MS;
        const attempts = withinWindow ? state.attempts : 0;

        if (attempts >= RECOVERY_MAX_ATTEMPTS) {
            writeRecoveryState({ attempts, lastAttempt: now });
            return {
                meta: {
                    recoveryAction: 'request-parent-refresh',
                    attempt: attempts,
                    maxAttempts: RECOVERY_MAX_ATTEMPTS,
                },
            };
        }

        const nextAttempts = attempts + 1;
        const nextState: RecoveryState = { attempts: nextAttempts, lastAttempt: now };
        const snapshot = JSON.stringify(nextState);

        return {
            meta: {
                recoveryAction: 'self-reload',
                attempt: nextAttempts,
                maxAttempts: RECOVERY_MAX_ATTEMPTS,
            },
            execute: () => {
                writeRecoveryState(nextState);
                clearClientCaches(snapshot).finally(() => {
                    window.location.replace(window.location.href);
                });
            },
        };
    };
    const handleError = (rawError: AnyError, extraContext: Record<string, unknown> = {}) => {
        if (!rawError) return;

        const statusCode =
            toNumber(extraContext.statusCode as number | string | null) ??
            extractStatusCode(rawError);
        const fatalError = statusCode && statusCode >= 500;

        const error = toError(rawError);
        if (shouldDeduplicate(extraContext)) {
            const signature = `${statusCode}:${error.message}:${extraContext.source ?? 'unknown'}`;
            if (reportedSignatures.has(signature)) return;
            reportedSignatures.add(signature);
        }

        const recoveryPlan = createRecoveryPlan();
        const payload = {
            statusCode,
            message: error.message,
            stack: error.stack,
            severity: fatalError ? 'fatal' : 'error',
            kind: fatalError ? 'server' : 'client',

            path: window.location?.pathname,
            timestamp: Date.now(),
            ...extraContext,
            recovery: recoveryPlan?.meta,
        };

        reportToDatadog(error, payload);
        notifyParent(payload);
        // this needst to be called only if the error is fatal
        if (fatalError) recoveryPlan?.execute?.();
    };
    const reportClientError: ReportClientError = (error, extraContext = {}) => {
        handleError(error, extraContext);
    };

    nuxtApp.hook('app:error', (error) => {
        reportClientError(error as AnyError, { source: 'nuxt:app' });
    });

    nuxtApp.hook('vue:error', (error, instance, info) => {
        reportClientError(error as AnyError, {
            source: 'vue',
            info,
            component: instance?.type?.name,
        });
    });

    window.addEventListener(
        'webglcontextlost',
        (event) => {
            const statusMessage =
                typeof event === 'object' && 'statusMessage' in event
                    ? (event as WebGLContextEvent).statusMessage
                    : undefined;

            reportClientError(new Error('WebGL context lost'), {
                source: 'webglcontextlost',
                statusCode: 503,
                statusMessage,
            });
        },
        { capture: true }
    );

    window.addEventListener(
        'error',
        (event) => {
            reportClientError((event as ErrorEvent).error ?? event, { source: 'window:error' });
        },
        { capture: true }
    );

    window.addEventListener('unhandledrejection', (event) => {
        reportClientError((event as PromiseRejectionEvent).reason ?? event, {
            source: 'unhandledrejection',
        });
    });

    return {
        provide: {
            reportClientError,
        },
    };
});
